import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Pencil, ChevronDown, ChevronUp } from "lucide-react";
import { ReviewValueRow } from "./ReviewValueRow";
import { ProductListEditor } from "./ProductListEditor";
import { useLanguage } from "@/contexts/LanguageContext";
import { getCategoryLabel } from "@/translations";
import { ORDER_CATEGORIES, CURRENCY } from "@/lib/constants";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { ParsedOrderData } from "@/types/order";

interface Product {
  name: string;
  quantity?: string;
  unit?: string;
  price?: string;
}

interface OrderReviewSummaryProps {
  data: ParsedOrderData;
  onDataChange: (data: ParsedOrderData) => void;
  onStartOver: () => void;
  className?: string;
}

/**
 * OrderReviewSummary - Review surface for Smart Input post-parse
 * 
 * UX Spec:
 * - Review surfaces look like summary blocks, NOT form cards
 * - Read-only values by default, edit on click
 * - Inline autosave to draft state
 * - Products displayed as structured list with "Edit items" toggle
 * - No heavy borders/frames - use spacing + dividers
 */
export function OrderReviewSummary({
  data,
  onDataChange,
  onStartOver,
  className,
}: OrderReviewSummaryProps) {
  const { language, t } = useLanguage();
  const [isEditingProducts, setIsEditingProducts] = useState(false);

  // Convert products from ParsedOrderData format to ProductListEditor format
  const products: Product[] = data.products || [];
  
  const handleProductsChange = (newProducts: Product[]) => {
    onDataChange({
      ...data,
      products: newProducts.map(p => ({
        name: p.name,
        quantity: p.quantity,
        unit: p.unit,
        price: p.price,
      })),
    });
  };

  // Calculate total from products if not set
  const calculatedTotal = products.reduce((sum, p) => {
    const qty = parseFloat(p.quantity || "1") || 1;
    const price = parseFloat(p.price || "0") || 0;
    return sum + (qty * price);
  }, 0);

  const displayTotal = data.total_price ?? calculatedTotal;

  return (
    <div className={cn("space-y-5", className)}>
      {/* Header - minimal, not framed */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-foreground">
          {t("orders.labels.extractedData")}
        </h3>
        <button
          type="button"
          onClick={onStartOver}
          className="text-xs text-muted-foreground/70 hover:text-foreground transition-colors"
        >
          {t("orders.labels.startOver")}
        </button>
      </div>

      {/* Order Reference - only if exists */}
      {data.order_number && (
        <div className="pb-3 border-b border-border">
          <ReviewValueRow
            label={t("orders.labels.orderNumber")}
            value={data.order_number}
            onValueChange={(v) => onDataChange({ ...data, order_number: v })}
            placeholder="#"
          />
        </div>
      )}

      {/* Customer Info Section */}
      <div className="space-y-0">
        <ReviewValueRow
          label={t("orders.labels.realName")}
          value={data.customer_name}
          onValueChange={(v) => onDataChange({ ...data, customer_name: v })}
          placeholder={t("orders.placeholders.fullName")}
        />
        <ReviewValueRow
          label={t("orders.labels.phoneIcon")}
          value={data.customer_phone}
          onValueChange={(v) => onDataChange({ ...data, customer_phone: v })}
          placeholder={t("orders.placeholders.customerPhone")}
          type="tel"
        />
        {data.social_name && (
          <ReviewValueRow
            label={t("orders.labels.socialName")}
            value={data.social_name}
            onValueChange={(v) => onDataChange({ ...data, social_name: v })}
            placeholder="@username"
          />
        )}
        <ReviewValueRow
          label={t("orders.labels.deliveryAddress")}
          value={data.address}
          onValueChange={(v) => onDataChange({ ...data, address: v })}
          placeholder={t("orders.placeholders.address")}
        />
      </div>

      {/* Products Section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            {t("orders.labels.products")}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setIsEditingProducts(!isEditingProducts)}
            className="h-7 text-xs gap-1.5"
          >
            {isEditingProducts ? (
              <>
                <ChevronUp className="h-3.5 w-3.5" />
                {t("common.close")}
              </>
            ) : (
              <>
                <Pencil className="h-3.5 w-3.5" />
                {t("bulk.actions.edit")}
              </>
            )}
          </Button>
        </div>

        {isEditingProducts ? (
          <div className="rounded-lg bg-muted/30 p-3">
            <ProductListEditor
              products={products.map(p => ({
                name: p.name,
                quantity: String(p.quantity ?? "1"),
                unit: p.unit || "",
                price: String(p.price ?? ""),
              }))}
              onProductsChange={(newProducts) =>
                handleProductsChange(newProducts.map(p => ({
                  name: p.name,
                  quantity: p.quantity,
                  unit: p.unit,
                  price: p.price,
                })))
              }
            />
          </div>
        ) : (
          <div className="rounded-lg bg-muted/30 p-3 space-y-1.5">
            {products.length > 0 ? (
              products.map((product, idx) => {
                const priceValue = product.price;
                const hasPrice = priceValue !== null && priceValue !== undefined && priceValue !== "";
                
                return (
                  <div key={idx} className="flex items-center justify-between text-sm">
                    <span className="text-foreground">
                      • {product.name}
                      {product.quantity && ` × ${product.quantity}`}
                      {product.unit && ` ${product.unit}`}
                    </span>
                    {hasPrice && (
                      <span className="font-medium text-foreground tabular-nums">
                        {priceValue} {CURRENCY.symbol}
                      </span>
                    )}
                  </div>
                );
              })
            ) : (
              <p className="text-sm text-muted-foreground italic">
                {t("orders.placeholders.noProducts") || "No products"}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Category - uses select, not inline edit */}
      <div className="flex items-center justify-between py-2.5 border-b border-border/50">
        <span className="text-sm text-muted-foreground">
          {t("orders.labels.category")}
        </span>
        <Select
          value={data.category || ""}
          onValueChange={(v) => onDataChange({ ...data, category: v })}
        >
          <SelectTrigger className="h-8 w-auto min-w-[120px] border-none bg-transparent font-medium text-right pr-2">
            <SelectValue placeholder={t("orders.placeholders.selectCategory")} />
          </SelectTrigger>
          <SelectContent>
            {ORDER_CATEGORIES.map((cat) => (
              <SelectItem key={cat} value={cat}>
                {getCategoryLabel(cat, language)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Total Price */}
      <ReviewValueRow
        label={t("orders.labels.totalPrice")}
        value={displayTotal || undefined}
        displayValue={displayTotal ? `${displayTotal.toFixed(2)}` : undefined}
        suffix={CURRENCY.symbol}
        onValueChange={(v) => onDataChange({ ...data, total_price: parseFloat(v) || undefined })}
        type="number"
      />

      {/* Optional: CSR / Posted By */}
      {data.csr_name && (
        <ReviewValueRow
          label={t("orders.labels.postedBy")}
          value={data.csr_name}
          onValueChange={(v) => onDataChange({ ...data, csr_name: v })}
        />
      )}

      {/* Comments */}
      {data.comments && (
        <ReviewValueRow
          label={t("orders.labels.commentsIcon")}
          value={data.comments}
          onValueChange={(v) => onDataChange({ ...data, comments: v })}
          placeholder={t("orders.placeholders.deliveryNotes")}
        />
      )}
    </div>
  );
}
